import _ from 'lodash';
import { LocalStorage, LocalStorageKeys } from '@/utils/local-storage';
import * as Sentry from '@sentry/vue';
import analytics from '@/utils/analytics';
import { EVENT_TYPES } from '@/data/lexisnexis-types';
import { auth0 } from '@/utils/auth0';

import {
  isCountryWithPostalCode,
  isPassportRequiredForCountry,
  COUNTRY_CODES,
} from '@/data/country-code-with-names';
import { buildApiActions } from '@/utils/vuex-api-utils';
import { PRODUCT_SEGMENT } from '@/data/product-segments';
import {
  isAuthorizedOwner,
  OWNER_ROLES,
  ENTITY_TYPE,
} from '@clearbanc/data-common-types';
import { Status } from '@clearbanc/baus-sdk';

import {
  usersApi,
  userInvitesApi,
  businessApi,
} from '../../utils/baus-request';
import { stateOptionsForCountry } from '../../utils/local';

const allPropsFilled = (obj, props) =>
  obj && _.every(props, (p) => !_.isNil(obj[p]));

function getProfileCountry(profile) {
  const userCountries = [
    _.get(profile, 'address.country'),
    profile.citizenship,
  ];
  if (userCountries.includes('US')) return 'US';
  if (userCountries.includes('CA')) return 'CA';
  return profile.citizenship || 'US';
}

export const isAddressComplete = (address) => {
  const addressRequirements = ['line1', 'city', 'country'];
  if (isCountryWithPostalCode(address?.country))
    addressRequirements.push('postalCode');
  if (stateOptionsForCountry(address?.country)) {
    addressRequirements.push('state');
  }
  return allPropsFilled(address, addressRequirements);
};

export const hasRequiredIdentification = (user) => {
  if (['US', 'CA'].includes(user.citizenship) && !user.personalTaxId) {
    return false;
  } else if (
    isPassportRequiredForCountry(getProfileCountry(user)) &&
    !user.passportNumber
  ) {
    return false;
  }
  return true;
};

/* ----  Helpers used in mutations below ---- */

/**
 * Updates the user state with the provided data.
 */
function setUserState(state, user) {
  const parsedUserId = Number(user.id);
  const authenticatedUserid = Number(auth0.user?.value?.userId);
  if (
    (user.id && Number.isNaN(parsedUserId)) ||
    (state.user.id && state.user.id !== parsedUserId) ||
    isNaN(authenticatedUserid) ||
    authenticatedUserid !== parsedUserId
  ) {
    Sentry.captureMessage('Requested to set user state to invalid user');
    return;
  }

  state.user = _.omit(user, ['id', 'business']);
  state.user.id = parsedUserId;
  if (!state.user.address) state.user.address = {};
  if (!state.user.shippingAddress) state.user.shippingAddress = {};
}

function setBusinessState(state, business) {
  state.business = business;
  if (!state.business.address) {
    state.business.address = {
      city: state.business.addressCity,
      country: state.business.addressCountryCode,
      line1: state.business.addressStreet1,
      postalCode: state.business.addressPostalCode,
      state: state.business.addressState,
    };
  }
  if (!state.business.shippingAddress) state.business.shippingAddress = {};
  if (!state.business.truliooSearchDetails)
    state.business.truliooSearchDetails = {};
}

function setOwnersState(state, owners) {
  state.owners = (owners ?? [])
    .filter((user) => Number(user.id) !== Number(state.user.id))
    .sort((a, b) => a.fullName.localeCompare(b.fullName));
}

export default {
  state: {
    user: {},
    owners: [],
    business: {},
    userInvites: [],
  },
  getters: {
    oauthSignup: (state) =>
      !!(
        state.user.oauthSignup ||
        LocalStorage.getItem(LocalStorageKeys.OAUTH_SIGNUP)
      ),
    // these ids used by submodules to know what ID to use in URLS
    userIsLoggedIn: (state) => !_.isEmpty(state.user),
    userId: (state, getters) => getters.user.id,
    businessId: (state) => state.business.id,
    businessName: (state) => state.business.name,
    user: (state) => ({
      ...state.user,
      id: state.user.id?.toString(),
    }),
    business: (state, getters) => ({
      ...state.business,
      owners: getters.businessOwners,
    }),
    userInvites: (state) => state.userInvites,
    businessCorpCountry: (state) => state.business.corpCountry,
    isUSBusiness: (state) => state.business.corpCountry === COUNTRY_CODES.US,
    isCABusiness: (state) => state.business.corpCountry === COUNTRY_CODES.CA,
    isAMERBusiness: (state) =>
      [COUNTRY_CODES.US, COUNTRY_CODES.CA].includes(state.business.corpCountry),
    isUKBusiness: (state) => state.business.corpCountry === COUNTRY_CODES.GB,
    productSegmentLabel(state) {
      const { label } = state.business.productSegment || {};
      return label;
    },
    isCoreUser(state, getters) {
      return getters.productSegmentLabel === PRODUCT_SEGMENT.CORE;
    },
    isB2BMerchant: (state, getters) =>
      [PRODUCT_SEGMENT.MRR_FACTORING, PRODUCT_SEGMENT.EXTEND_RUNWAY].includes(
        getters.productSegmentLabel,
      ),
    isIndiegogoMerchant: (state, getters) =>
      [PRODUCT_SEGMENT.INDIEGOGO].includes(getters.productSegmentLabel),
    userHasForceBnplFlag: (state) => !!state.user.featureFlags?.forceBnpl,
    isPlaidConnectSupportedCountry: (state) => {
      return ['CA', 'GB', 'US'].includes(state.business.corpCountry);
    },
    isAuthorizedSignatory: (_state, getter) =>
      getter.user.id === getter.authorizedSignatory?.id,
    authorizedSignatory: (state, getters) => {
      return getters.allUsers.find((owner) =>
        owner.jobRoles?.includes(OWNER_ROLES.SIGNATORY),
      );
    },
    businessHasAuthorizedSignatory: (state, getters) => {
      return !!getters.authorizedSignatory;
    },
    allAuthorizedSignatories: (state, getters) => {
      return getters.allUsers.filter((owner) =>
        owner.jobRoles?.includes(OWNER_ROLES.SIGNATORY),
      );
    },
    // BUSINESS DETAILS ////////////////////////////////////////////////////////////////////////////
    businessDetailsIssues: (state) => {
      const b = state.business;
      const a = b.address || {};
      const s = b.shippingAddress || {};
      const issues = [];
      const required = {
        name: 'business name',
        type: 'business type',
        corpCountry: 'home country',
        corpType: 'legal business structure',
        phone: 'business phone number',
        corpState: 'incorporation state',
      };
      if (b.corpCountry === 'US') {
        required.federalTaxId = 'federal tax id (EIN)';
      } else {
        required.registrationNumber = 'business registration number';
      }
      if (['US', 'CA'].includes(b.corpCountry)) {
        required.corpDate = 'incorporation date';
      }
      _.each(required, (label, prop) => {
        if (!b[prop]) issues.push(`Please provide your ${label}`);
      });

      const addressComplete = !!(a.line1 && a.city && a.state && a.country);
      const postalCodeRequireIncomplete = !!(
        isCountryWithPostalCode(a.country) && !a.postalCode
      );
      if (!addressComplete || postalCodeRequireIncomplete) {
        issues.push('Please complete your business address');
      }

      if (!b.shippingAddressSame) {
        const shippingAddressComplete = !!(
          s.line1 &&
          s.city &&
          s.state &&
          s.country
        );
        const shippingPostalCodeRequireIncomplete = !!(
          isCountryWithPostalCode(s.country) && !s.postalCode
        );
        if (!shippingAddressComplete || shippingPostalCodeRequireIncomplete) {
          issues.push('Please complete your shipping address');
        }
      }
      return issues;
    },
    businessDetailsComplete: (state, getters) =>
      getters.businessDetailsIssues.length === 0,
    businessOwners: (state) => state.owners ?? [],
    allUsers: (state, getters, rootState, rootGetters) => {
      if (!getters.user) {
        return [];
      }
      return [getters.user, ...getters.businessOwners];
    },
    eligibleOwners: (state, getters, rootState, rootGetters) => {
      return getters.allUsers.filter((owner) =>
        isAuthorizedOwner(owner.jobTitle, owner.jobRoles),
      );
    },
    allBusinessProfiles: (state, getters, rootState, rootGetters) => {
      const noPendingOwners = (getters.eligibleOwners ?? []).filter((owner) => {
        // Filter out owners who have not accepted their invite (Auth0)
        return owner.status === Status.Active;
      });

      return noPendingOwners;
    },
    primaryUser: (_state, getters) =>
      getters.allUsers.find((user) => user.isPrimary),
    hasSignatory: (state, getters) => {
      return !!getters.authorizedSignatory;
    },
    isOwnerInfoComplete: (state, getters) => (ownerId) => {
      const owner = getters.eligibleOwners.find(
        (owner) => owner.id === ownerId,
      );
      return owner?.profileCompleted ?? false;
    },
    fiscalStartDate: (state) => state.business.fiscalStartDate,
    businessOnPlaidBypass: (state, getters, rootState, rootGetters) =>
      state.business.plaidBypass?.bank_id && rootGetters.isBypassPlaidEnabled,
    businessHasPendingBillContractOrActiveAdvance: (
      state,
      getters,
      rootState,
      rootGetters,
    ) => {
      return (
        rootGetters.anyBillContractsPending ||
        rootGetters.activeAdvances.length > 0
      );
    },
    eventTypeBasedOnLoginsCount: (state) => {
      return auth0?.user?.value?.loginsCount === 1
        ? EVENT_TYPES.ACCOUNT_CREATION
        : EVENT_TYPES.LOGIN;
    },
  },
  ...buildApiActions(
    {
      GET_USER: {
        action: (ctx, payload) => ({
          requestFunc: () =>
            usersApi.findUserById({
              userId: payload.userId || ctx.state.user.id,
            }),
          returnResponse: true,
        }),
        mutation: (state, { response }) => {
          analytics.identify(response.id);
          setUserState(state, response);
          // add user/business ids to Sentry errors
          Sentry.setUser({
            id: response.id,
            businessId: response.businessId,
            email: response.email,
          });
        },
      },
      GET_BUSINESS: {
        action: (ctx, payload) => {
          return {
            method: 'get',
            url: `/businesses/${payload.businessId}`,
            returnResponse: true,
          };
        },
        mutation: (state, { response }) => {
          setBusinessState(state, response);
        },
      },
      SET_PLAID_BYPASS: {
        action: (ctx, payload) => {
          const { businessId, ...rest } = payload;
          return {
            method: 'patch',
            url: `/businesses/${businessId}/plaid-bypass`,
            params: rest,
          };
        },
        mutation: (state, { response }) => {
          setBusinessState(state, response);
        },
      },
      GET_USERS_FOR_BUSINESS: {
        action: (ctx) => ({
          requestFunc: () =>
            usersApi.findByAttributes({
              businessId: ctx.state.user.businessId,
            }),
          returnResponse: true,
          afterSuccess(response) {
            // If there are secondary users, we will fetch their invites
            if (response.length) {
              ctx.dispatch('api-GET_USER_INVITES');
            }
          },
        }),
        mutation: (state, { response }) => {
          setOwnersState(state, response);
        },
      },
      GET_USER_INVITES: {
        action: (ctx) => ({
          requestFunc: () => userInvitesApi.findUserInvites(),
        }),
        mutation: (state, { response }) => {
          state.userInvites = response;
        },
      },
      UPDATE_USER: {
        action: (ctx, payload) => ({
          requestFunc: () =>
            usersApi.updateUser({
              id: payload.id ?? ctx.getters.userId,
              patchUserDto: payload,
            }),
          async afterSuccess() {
            if (payload.overrideSignatories) {
              // overrideSignatories will remove the signatories jobTitle from other users
              // Because other users were potentially updated, we need to make sure our state has the latest users
              // Load authenticated user + all other users for the business
              await ctx.dispatch('api-GET_USER', {
                userId: ctx.getters.userId,
              });
              await ctx.dispatch('api-GET_USERS_FOR_BUSINESS');
            }

            if (ctx.getters.globalFeatureFlags?.lexisnexis_ddp) {
              await ctx.dispatch('api-GET_VERIFICATION', {
                businessId: ctx.getters.businessId,
                entityId: ctx.getters.businessId,
                entityType: ENTITY_TYPE.BUSINESS,
              });

              if (!payload.overrideSignatories) {
                await ctx.dispatch('api-GET_USERS_FOR_BUSINESS');
              }
            }
          },
        }),
        mutation: (state, { response }) => {
          if (Number(state.user.id) === Number(response.id)) {
            // If updating authenticated user, update the user state
            setUserState(state, response);
          } else {
            // If updating a non-authenticated user, update the owners state
            const nonUpdatedOwners = state.owners.filter(
              (owner) => owner.id !== response.id,
            );
            setOwnersState(state, [...nonUpdatedOwners, response]);
          }
        },
      },
      UPDATE_USER_PROFILE: {
        action: (ctx, payload) => ({
          requestFunc: () =>
            usersApi.updateUser({
              id: payload.id,
              businessId: ctx.getters.businessId,
              patchUserDto: payload,
            }),
        }),
        mutation: (state, { response }) => {
          if (Number(state.user.id) === Number(response.id)) {
            setUserState(state, response);
          } else {
            const nonUpdatedOwners = state.owners.filter(
              (owner) => owner.id !== response.id,
            );
            setOwnersState(state, [...nonUpdatedOwners, response]);
          }
        },
      },
      UPDATE_BUSINESS: {
        action: (ctx, payload) => {
          return {
            method: 'patch',
            url: `/users/${ctx.state.user.id}/business`,
            params: _.omit(payload, ['id', 'truliooKybDetails']),
            async afterSuccess() {
              if (ctx.getters.globalFeatureFlags?.lexisnexis_ddp) {
                await Promise.allSettled([
                  ctx.dispatch('api-GET_VERIFICATION', {
                    businessId: ctx.getters.businessId,
                    entityId: ctx.getters.businessId,
                    entityType: ENTITY_TYPE.BUSINESS,
                  }),
                  ctx.dispatch('api-GET_USER', {
                    userId: ctx.getters.userId,
                  }),
                  ctx.dispatch('api-GET_USERS_FOR_BUSINESS', {
                    userId: ctx.getters.userId,
                  }),
                ]);
              }
            },
          };
        },
        mutation: (state, { response }) => {
          setBusinessState(state, response);
        },
      },
      CREATE_NON_PRIMARY_USER: {
        action: (ctx, payload) => {
          return {
            requestFunc: () =>
              usersApi.create({
                createRequest: {
                  ...payload,
                  businessId: ctx.getters.businessId,
                  id: undefined,
                },
              }),
            returnResponse: true,
          };
        },
      },
      CREATE_USER_INVITE: {
        action: (ctx, payload) => ({
          requestFunc: () =>
            userInvitesApi.createInvite({
              createUserInviteDto: {
                invitedUserId: payload.id,
                type: payload.type,
              },
            }),
          async afterFailure() {
            await ctx.dispatch('api-GET_USER_INVITES');
          },
          async afterSuccess() {
            await ctx.dispatch('api-GET_USER_INVITES');
          },
        }),
      },
      INVITE_USER: {
        action: (ctx, payload) => ({
          requestFunc: () =>
            businessApi.createUserInvite(
              {
                id: payload.businessId,
              },
              {
                data: payload.inviteNewUserDto ?? payload.inviteExistingUserDto,
              },
            ),
          returnResponse: true,
        }),
        mutation: (state, { response }) => {
          const existingUserIndex = state.owners.findIndex(
            (owner) => owner.id === response.id,
          );
          if (existingUserIndex >= 0) {
            const updatedOwners = [...state.owners];
            updatedOwners[existingUserIndex] = response;
            setOwnersState(state, updatedOwners);
          } else {
            setOwnersState(state, [...state.owners, response]);
          }
        },
      },
      UPDATE_USER_WITHOUT_AUTH: {
        action: (ctx, payload) => ({
          requestFunc: () =>
            usersApi.confirmUserInvite({
              uuid: payload.uuid,
              patchUserInviteDto: payload,
            }),
        }),
      },
      DELETE_USER: {
        action: (ctx, payload) => {
          return {
            requestFunc: () =>
              usersApi.deleteUser({
                id: payload.id,
              }),
          };
        },
        mutation: (state, { response }) => {
          const nonDeletedOwners = state.owners.filter(
            (owner) => owner.id !== response.id,
          );
          setOwnersState(state, nonDeletedOwners);
        },
      },
      REMOVE_PERSONAL_DATA: {
        action: (ctx, payload) => ({
          method: 'post',
          url: `/users/${ctx.state.user.id}/request-remove-personal-data`,
          params: payload,
        }),
        mutation: (state, { response }) => {
          state.user.deletedAt = new Date();
        },
      },
      EXPORT_PERSONAL_DATA: {
        action: (ctx, payload) => ({
          method: 'post',
          url: `/users/${ctx.state.user.id}/export-personal-data`,
          params: payload,
        }),
      },
      STORE_USER_ATTRIBUTION: {
        action: (ctx, payload) => {
          return {
            method: 'post',
            url: `/tracking/user-attribution`,
            params: {
              userId: payload.userId,
              urlParams: payload.urlParams,
              authType: payload.authType,
            },
          };
        },
      },
    },
    {
      mutations: {
        ADD_SECONDARY_USER: (state, params) => {
          setOwnersState(state, [...state.owners, params]);
        },
        UPDATE_BUSINESS_PARAMS: (state, params) => {
          setBusinessState(state, { ...state.business, ...params });
        },
        UPDATE_USER_PARAMS: (state, params) => {
          setUserState(state, { ...state.user, ...params });
        },
        UPDATE_BUSINESS_ADDRESS_PARAMS: (state, params) => {
          state.business.address = {
            ...state.business.address,
            ...params,
          };
        },
        UPDATE_BUSINESS_SHIPPING_ADDRESS_PARAMS: (state, params) => {
          state.business.shippingAddress = {
            ...state.business.shippingAddress,
            ...params,
          };
        },
        SET_CURRENT_USER: (state, user) => {
          setUserState(state, user);
          // add user/business ids to Sentry errors
          Sentry.setUser({
            id: user.id,
            businessId: user.businessId,
            email: user.email,
          });
        },
        USER_LOGOUT: (state) => {
          // clear Vuex store state
          state.user = {};
          state.business = {};
          // reset analytics
          analytics.reset();
        },
      },
      actions: {
        ADD_SECONDARY_USER: ({ commit }, params) => {
          commit('ADD_SECONDARY_USER', params);
        },
        UPDATE_USER_PARAMS: ({ commit }, params) => {
          commit('UPDATE_USER_PARAMS', params);
        },
        UPDATE_BUSINESS_PARAMS: ({ commit }, params) => {
          commit('UPDATE_BUSINESS_PARAMS', params);
        },
      },
    },
  ),
};
